import { Box } from '@mui/material';
import * as R from 'ramda';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AnswerQuizApiErrorMessages,
  ComplianceTypes,
  Routes,
} from '@/constants';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  parseErrorMessageFromActionPayload,
  playerThunks,
  selectActiveChapterId,
  selectActiveModalParams,
  selectChapterProgress,
  selectEpisodeCompliance,
  selectIsEpisodeRedeemRequired,
  selectIsPlayerPlaying,
  selectPlayerSessionDetailState,
  selectSessionUserId,
  uiActions,
  useAnswerQuizMutation,
  useAppDispatch,
  useAppSelector,
  useFetchEpisodeChaptersQuery,
  useFetchEpisodeQuizzesQuery,
  useFindPlayerSessionByEpisodeIdQuery,
} from '@/store';
import {
  createRouteWithParams,
  isTestModeEnabled,
  SnackbarUtils,
} from '@/utils';

import { ChapterQuizModalContentComponent } from './ChapterQuizModalContent.component';
import { ChapterQuizModalSkeleton } from './ChapterQuizModalSkeleton';
import { chapterQuizModalStyles } from './styles';
import { ModalPage } from './types';

interface ChapterQuizModalContentProps {
  setIsOnCompletionStep: (value: boolean) => void;
  isOnCompletionStep: boolean;
}

export const ChapterQuizModalContent = ({
  setIsOnCompletionStep,
  isOnCompletionStep,
}: ChapterQuizModalContentProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isTestMode = isTestModeEnabled();

  const [selectedPageIndex, setSelectedPageIndex] = useState<
    number | undefined
  >();
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState<
    number | undefined
  >();
  const [submission, setSubmission] = useState<
    | {
        answerIndex: number;
        isCorrect: boolean;
      }
    | undefined
  >();

  const userId = useAppSelector(selectSessionUserId);
  const { episodeId, chapterId, showFirstIncompleteQuiz } =
    useAppSelector(selectActiveModalParams) || {};
  const isPlayerPlaying = useAppSelector(selectIsPlayerPlaying);
  const activeChapterId = useAppSelector(selectActiveChapterId);

  const playerSessionDetails = useAppSelector(state =>
    selectPlayerSessionDetailState(state, episodeId),
  );
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, episodeId),
  );
  const isEpisodeRedeemRequired = useAppSelector(state =>
    selectIsEpisodeRedeemRequired(state, episodeId),
  );

  const { data: chapters, isLoading: isChaptersLoading } =
    useFetchEpisodeChaptersQuery(
      { episodeId, compliance },
      {
        skip: !episodeId,
      },
    );

  const { data: episodeQuizzes, isLoading: isQuizzesLoading } =
    useFetchEpisodeQuizzesQuery(
      {
        episodeId,
        compliance,
        testMode: isTestMode,
      },
      { skip: !episodeId },
    );

  useFindPlayerSessionByEpisodeIdQuery(
    { episodeId },
    { skip: !userId || !episodeId, refetchOnMountOrArgChange: true },
  );
  const [answerQuiz, { isLoading: isSubmitLoading }] = useAnswerQuizMutation();

  const modalPages = useMemo(() => {
    const formattedArray: ModalPage[] = [];
    if (!chapters || !episodeQuizzes) return formattedArray;
    chapters
      ?.filter(chapter => !chapter.isTrailer)
      .forEach((chapter, chapterIndex) => {
        const chapterQuizzes =
          episodeQuizzes?.filter(q => q.chapterId === chapter.chapterId) || [];
        if (!chapterQuizzes.length) {
          formattedArray.push({
            chapter,
            chapterIndex,
          });
          return;
        }
        chapterQuizzes.forEach((quiz, index) => {
          const sessionChapter = playerSessionDetails?.chapters?.find(
            sessionChapter => sessionChapter.chapterId === chapter.chapterId,
          );
          const isQuizCompleted = !!(
            sessionChapter?.state === 'verified' ||
            sessionChapter?.state === 'completed' ||
            sessionChapter?.completedQuizIds?.includes(quiz.postgresQuizId)
          );

          formattedArray.push({
            chapter,
            chapterIndex,
            quiz,
            isQuizCompleted,
            quizCountLabel:
              chapterQuizzes.length > 1
                ? `${index + 1}/${chapterQuizzes.length}`
                : undefined,
          });
        });
      });
    return formattedArray;
  }, [chapters, episodeQuizzes, playerSessionDetails]);

  const selectedPage = !R.isNil(selectedPageIndex)
    ? modalPages[selectedPageIndex]
    : null;

  const userChapter = useAppSelector(state =>
    selectedPage
      ? selectChapterProgress(state, {
          episodeId: selectedPage.chapter.episodeId,
          chapterId: selectedPage.chapter.chapterId,
        })
      : null,
  );
  const isChapterCompleted = !!userChapter?.completed;

  useEffect(() => {
    // Initialize starting page
    if (!R.isNil(selectedPageIndex) || !modalPages.length) return;

    let index = 0;
    if (showFirstIncompleteQuiz) {
      const firstIncompleteQuizIndex = modalPages.findIndex(
        page => page.quiz && !page.isQuizCompleted,
      );
      if (firstIncompleteQuizIndex > -1) index = firstIncompleteQuizIndex;
    } else if (chapterId) {
      const chapterIndices = modalPages.reduce((acc: number[], page, i) => {
        if (page.chapter.chapterId === chapterId) acc.push(i);
        return acc;
      }, []);
      const firstIncompleteQuizIndex =
        chapterIndices.find((i: number) => !modalPages[i].isQuizCompleted) ??
        -1;
      if (firstIncompleteQuizIndex > -1) {
        index = firstIncompleteQuizIndex;
      } else if (chapterIndices[0] > -1) {
        index = chapterIndices[0];
      }
    }

    setSelectedPageIndex(index);
  }, [modalPages, chapterId, selectedPageIndex, showFirstIncompleteQuiz]);

  useEffect(() => {
    const selectedPage = R.isNil(selectedPageIndex)
      ? null
      : modalPages[selectedPageIndex];
    if (selectedPage?.quiz) {
      const question = selectedPage.chapter.chapterPosition.toString();
      AnalyticsService.quizView({
        chapterId: selectedPage.chapter.chapterId,
        quizId: selectedPage.quiz?.quizId,
        question: selectedPage.quizCountLabel
          ? question + ' - ' + selectedPage.quizCountLabel
          : question,
        isAvailable: isChapterCompleted,
        isAnswered: !!selectedPage.isQuizCompleted,
      });
    }
  }, [modalPages, selectedPageIndex, isChapterCompleted]);

  useEffect(() => {
    setSelectedAnswerIndex(undefined);
  }, [selectedPageIndex]);

  useEffect(() => {
    setSubmission(undefined);
  }, [selectedAnswerIndex]);

  const isDataLoading = isChaptersLoading || isQuizzesLoading;
  if (isDataLoading || R.isNil(selectedPageIndex) || !selectedPage) {
    return (
      <Box sx={chapterQuizModalStyles.modalContainer}>
        <ChapterQuizModalSkeleton />
      </Box>
    );
  }

  const { chapter, quiz, isQuizCompleted } = selectedPage;

  const onPreviousPage = () => {
    if (selectedPageIndex === 0) return;
    setSelectedPageIndex(selectedPageIndex - 1);
  };

  const onNextPage = () => {
    if (selectedPageIndex === modalPages.length - 1) {
      setIsOnCompletionStep(true);
      return;
    }
    setSelectedPageIndex(selectedPageIndex + 1);
  };

  const onClose = () => {
    dispatch(uiActions.closeActiveModal());
    setTimeout(() => {
      setIsOnCompletionStep(false);
    }, 1000);
  };

  const onSubmitQuiz = async () => {
    if (R.isNil(selectedAnswerIndex) || !quiz || !playerSessionDetails)
      return false;

    const answerId = quiz.answers[selectedAnswerIndex].id;
    const analyticsPayload = {
      chapterId: chapter.chapterId,
      quizId: quiz.quizId,
      answer: answerId,
    };

    try {
      const data = await answerQuiz({
        sessionId: playerSessionDetails.sessionId,
        chapterId: chapter.chapterId,
        quizId: quiz.postgresQuizId,
        answerId,
      }).unwrap();

      if (!data?.pass) {
        AnalyticsService.quizAttempt({ ...analyticsPayload, pass: false });
        setSubmission({ answerIndex: selectedAnswerIndex, isCorrect: false });
      } else {
        AnalyticsService.quizAttempt({ ...analyticsPayload, pass: true });
        setSubmission({ answerIndex: selectedAnswerIndex, isCorrect: true });

        // If quiz is passed and it was the final incomplete quiz, log episode verified event
        const nonVerifiedChapters = playerSessionDetails.chapters.filter(
          chapter => !chapter.isTrailer && chapter.state !== 'verified',
        );
        if (
          compliance === ComplianceTypes.CPD &&
          nonVerifiedChapters.length === 1 &&
          nonVerifiedChapters[0].chapterId === chapter.chapterId
        ) {
          AnalyticsService.episodeComplete({
            episodeId,
          });
        }
      }
      return true;
    } catch (error) {
      const errorMessage = parseErrorMessageFromActionPayload(error);
      const userMessage =
        AnswerQuizApiErrorMessages[
          errorMessage as keyof typeof AnswerQuizApiErrorMessages
        ];

      SnackbarUtils.warning(
        `Failed to submit quiz, ${userMessage || 'please try again or contact support'}.`,
      );
      return false;
    }
  };

  const referrer = {
    component: EVENT_CONSTANTS.COMPONENT.CHAPTER_QUIZ_MODAL,
  };

  const onPlayChapter = () => {
    dispatch(
      playerThunks.chapterPlayPressed({
        activeChapter: {
          episodeId: chapter.episodeId,
          chapterId: chapter.chapterId,
        },
        referrer,
        location: {
          ...referrer,
          button: EVENT_CONSTANTS.BUTTON.PLAY_CHAPTER,
        },
      }),
    );
  };

  const onShowAssessment = () => {
    onClose();
    if (isEpisodeRedeemRequired) {
      dispatch(
        playerThunks.initiateRedemption({
          episodeId,
          location: {
            ...referrer,
            button: EVENT_CONSTANTS.BUTTON.ASSESSMENT,
          },
        }),
      );
    } else {
      const assessmentUrl = createRouteWithParams(Routes.EPISODE_ASSESSMENT, {
        episodeId,
      });
      navigate(assessmentUrl);
    }
  };

  const isChapterPlaying =
    isPlayerPlaying && activeChapterId === chapter?.chapterId;

  return (
    <ChapterQuizModalContentComponent
      onNextPage={onNextPage}
      onPreviousPage={onPreviousPage}
      onSelectAnswer={setSelectedAnswerIndex}
      onClearAnswer={() => setSelectedAnswerIndex(undefined)}
      onSubmitQuiz={onSubmitQuiz}
      onPlayChapter={onPlayChapter}
      onClose={onClose}
      showAssessment={onShowAssessment}
      pages={modalPages}
      selectedPageIndex={selectedPageIndex}
      selectedAnswerIndex={selectedAnswerIndex}
      isChapterCompleted={isChapterCompleted}
      isChapterPlaying={isChapterPlaying}
      isQuizCompleted={!!isQuizCompleted}
      isSubmitLoading={isSubmitLoading}
      submission={submission}
      compliance={compliance}
      isOnCompletionStep={isOnCompletionStep}
      episodeId={episodeId}
    />
  );
};
